import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';

import InputStyle from '../style.ts';

export const RangeFormat = {
  number: (v) => String(v),
  age: (v) => `${v} yrs`,
  minutes: (v) => `${v} min`,
  minutesToHourMin: (v) => {
    const h = Math.floor(v / 60);
    const m = v % 60;
    return h > 0 ? (m > 0 ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
  },
  seconds: (v) => {
    const h = Math.floor(v / 3600);
    const m = Math.floor((v % 3600) / 60);
    const s = v % 60;
    return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
  },
  currency:
    (symbol = '$') =>
    (v) =>
      `${symbol}${v.toLocaleString()}`,
  percent: (v) => `${Math.round(v * 100)}%`,
  distance:
    (unit = 'km') =>
    (v) =>
      `${v} ${unit}`,
  timeOfDay: (v) => {
    const h = Math.floor(v / 60);
    const m = v % 60;
    const period = h < 12 ? 'AM' : 'PM';
    const displayH = h % 12 === 0 ? 12 : h % 12;
    return `${displayH}:${String(m).padStart(2, '0')} ${period}`;
  },
};

function resolveInputList(inputList) {
  if (!inputList) return {};
  const entry = Object.values(inputList).find(Boolean);
  if (!entry) return {};
  const { min, max, step, formatLabel, minLabel, maxLabel } = entry;
  return {
    ...(min !== undefined && { min }),
    ...(max !== undefined && { max }),
    ...(step !== undefined && { step }),
    ...(formatLabel !== undefined && { formatLabel }),
    ...(minLabel !== undefined && { minLabel }),
    ...(maxLabel !== undefined && { maxLabel }),
  };
}

export default function InputLinearRange({
  containerStyle = {},
  isEnabled = true,
  isHidden = false,
  isRequired = false,
  isValid = true,
  labelText = '',
  labelStyle = {},
  name,
  onChange,
  clearOnStatusChange = true,
  min: minProp = 0,
  max: maxProp = 100,
  step: stepProp = 1,
  formatLabel: formatLabelProp = RangeFormat.number,
  minLabel: minLabelProp,
  maxLabel: maxLabelProp,
  inputList,
  value = { min: null, max: null },
}) {
  const {
    min = minProp,
    max = maxProp,
    step = stepProp,
    formatLabel = formatLabelProp,
    minLabel = minLabelProp,
    maxLabel = maxLabelProp,
  } = resolveInputList(inputList);

  useEffect(() => {
    if (clearOnStatusChange && (!isEnabled || isHidden)) {
      onChange({ [name]: { min: null, max: null } });
    }
  }, [isEnabled, isHidden, clearOnStatusChange, onChange, name]);

  const trackRef = useRef(null);

  if (isHidden) return null;

  const resolvedMin = value.min ?? min;
  const resolvedMax = value.max ?? max;

  function pct(v) {
    return ((v - min) / (max - min)) * 100;
  }

  const leftPct = pct(resolvedMin);
  const rightPct = pct(resolvedMax);

  function handleMinChange(e) {
    const next = Math.min(Number(e.target.value), resolvedMax - step);
    onChange({ [name]: { min: next, max: resolvedMax } });
  }

  function handleMaxChange(e) {
    const next = Math.max(Number(e.target.value), resolvedMin + step);
    onChange({ [name]: { min: resolvedMin, max: next } });
  }

  const accent = isEnabled ? (isValid ? '#111827' : '#ef4444') : '#9ca3af';

  const accentBg = isEnabled ? (isValid ? '#f3f4f6' : '#fee2e2') : '#f9fafb';

  const accentBorder = isEnabled
    ? isValid
      ? '#d1d5db'
      : '#fca5a5'
    : '#e5e7eb';

  const styles = {
    wrapper: {
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      width: '100%',
    },
    trackWrapper: {
      position: 'relative',
      height: 28,
      display: 'flex',
      alignItems: 'center',
    },
    trackBg: {
      position: 'absolute',
      left: 0,
      right: 0,
      height: 4,
      borderRadius: 2,
      background: isEnabled ? '#e0e0e0' : '#f0f0f0',
      pointerEvents: 'none',
    },
    trackFill: {
      position: 'absolute',
      left: `${leftPct}%`,
      width: `${rightPct - leftPct}%`,
      height: 4,
      borderRadius: 2,
      background: accent,
      pointerEvents: 'none',
      transition: 'background 0.15s',
    },
    rangeInput: {
      position: 'absolute',
      width: '100%',
      height: 4,
      appearance: 'none',
      WebkitAppearance: 'none',
      background: 'transparent',
      pointerEvents: 'none',
      margin: 0,
      padding: 0,
      outline: 'none',
    },
    boundaryRow: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: 11,
      color: isEnabled ? '#9ca3af' : '#d1d5db',
      userSelect: 'none',
    },
    valueRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '2px 10px',
      borderRadius: 12,
      fontSize: 13,
      fontWeight: 600,
      background: accentBg,
      color: accent,
      border: `1px solid ${accentBorder}`,
      transition: 'all 0.15s',
    },
    dash: { color: '#9ca3af', fontSize: 13 },
  };

  const uid = `ilr-${name}`;
  const thumbCss = `
    .${uid}::-webkit-slider-thumb {
      -webkit-appearance: none;
      width: 18px; height: 18px;
      border-radius: 50%;
      border: 2px solid ${accent};
      background: #fff;
      cursor: ${isEnabled ? 'pointer' : 'not-allowed'};
      pointer-events: all;
      box-shadow: 0 1px 4px rgba(0,0,0,0.15);
      transition: border-color 0.15s, transform 0.1s;
    }
    .${uid}:enabled::-webkit-slider-thumb:hover { transform: scale(1.15); }
    .${uid}::-moz-range-thumb {
      width: 18px; height: 18px;
      border-radius: 50%;
      border: 2px solid ${accent};
      background: #fff;
      cursor: ${isEnabled ? 'pointer' : 'not-allowed'};
      pointer-events: all;
      box-shadow: 0 1px 4px rgba(0,0,0,0.15);
    }
    .${uid}:focus::-webkit-slider-thumb {
      box-shadow: 0 0 0 3px ${accentBorder};
    }
  `;

  return (
    <span style={{ ...InputStyle.container, ...containerStyle }}>
      <style>{thumbCss}</style>

      <label
        htmlFor={`${name}-min`}
        style={{ ...InputStyle.label(isEnabled), ...labelStyle }}
      >
        {labelText}
        {isRequired && <span style={{ color: 'red' }}>*</span>}
      </label>

      <div style={styles.wrapper}>
        <div style={styles.valueRow}>
          <span style={styles.badge}>{formatLabel(resolvedMin)}</span>
          <span style={styles.dash}>—</span>
          <span style={styles.badge}>{formatLabel(resolvedMax)}</span>
        </div>

        <div style={styles.trackWrapper} ref={trackRef}>
          <div style={styles.trackBg} />
          <div style={styles.trackFill} />

          <input
            className={uid}
            type="range"
            id={`${name}-min`}
            aria-label={`${labelText} minimum`}
            min={min}
            max={max}
            step={step}
            value={resolvedMin}
            disabled={!isEnabled}
            onChange={handleMinChange}
            style={{
              ...styles.rangeInput,
              zIndex: resolvedMin > max - step ? 5 : 3,
            }}
          />

          <input
            className={uid}
            type="range"
            id={`${name}-max`}
            aria-label={`${labelText} maximum`}
            min={min}
            max={max}
            step={step}
            value={resolvedMax}
            disabled={!isEnabled}
            onChange={handleMaxChange}
            style={{ ...styles.rangeInput, zIndex: 4 }}
          />
        </div>

        <div style={styles.boundaryRow}>
          <span>{minLabel ?? formatLabel(min)}</span>
          <span>{maxLabel ?? formatLabel(max)}</span>
        </div>
      </div>
    </span>
  );
}

const inputListEntryShape = PropTypes.shape({
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  step: PropTypes.number,
  formatLabel: PropTypes.func,
  minLabel: PropTypes.string,
  maxLabel: PropTypes.string,
});

InputLinearRange.propTypes = {
  containerStyle: PropTypes.object,
  isEnabled: PropTypes.bool,
  isHidden: PropTypes.bool,
  isRequired: PropTypes.bool,
  isValid: PropTypes.bool,
  labelText: PropTypes.string,
  labelStyle: PropTypes.object,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  clearOnStatusChange: PropTypes.bool,
  inputList: PropTypes.objectOf(inputListEntryShape),
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  formatLabel: PropTypes.func,
  minLabel: PropTypes.string,
  maxLabel: PropTypes.string,
  value: PropTypes.shape({
    min: PropTypes.number,
    max: PropTypes.number,
  }),
};
